import { ref, onMounted } from "vue";
import type { BuildInfo } from "@helpers/RemoteLogger";

// Standalone function for fetching build info (used in main.ts and version checker)
// Always cache-busts since version.json should always be fresh
export async function fetchBuildInfo(): Promise<BuildInfo | null> {
  try {
    const url = `/version.json?cache-bust=${Date.now()}`;
    const response = await fetch(url, { cache: "no-store" });
    if (!response.ok) {
      throw new Error(`Failed to fetch version info: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    const message = err instanceof Error ? err.message : "Failed to load build info";
    console.warn("Could not load build information:", err);
    throw new Error(message);
  }
}

export function useBuildInfo() {
  const buildInfo = ref<BuildInfo | null>(null);
  const loading = ref(true);
  const error = ref<string | null>(null);

  const fetchBuildInfoForComposable = async () => {
    try {
      loading.value = true;
      error.value = null;
      buildInfo.value = await fetchBuildInfo();
    } catch (err) {
      error.value = err instanceof Error ? err.message : "Failed to load build info";
    } finally {
      loading.value = false;
    }
  };

  onMounted(() => {
    fetchBuildInfoForComposable();
  });

  const getBuildAge = () => {
    if (!buildInfo.value) return null;

    const now = Date.now();
    const diffInHours = Math.floor((now - buildInfo.value.buildTimestamp) / (1000 * 60 * 60));

    if (diffInHours < 1) return "Less than an hour ago";
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours === 1 ? "" : "s"} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays === 1 ? "" : "s"} ago`;
  };

  const getShortCommitUrl = (baseUrl: string = "https://github.com/your-org/your-repo") => {
    if (!buildInfo.value) return null;
    return `${baseUrl}/commit/${buildInfo.value.gitCommitHashFull}`;
  };

  return {
    buildInfo,
    loading,
    error,
    fetchBuildInfo: fetchBuildInfoForComposable,
    getBuildAge,
    getShortCommitUrl,
  };
}
